import React, { memo, useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../stores/store";
import { PendingStatus } from "../model/energy-profile";
import { ApplicationRoutes } from "../routes";
import { useGetEnergyAccountQuery } from "../service/vending-service";
import { setEnergyProfileInfo } from "../stores/slices/customer-energy-profile-slice";
import { CustomerLoader } from "../pages/customer/components";

const EnergyProfileGuard: React.FC = memo(() => {
	const dispatch = useDispatch();
	const location = useLocation();
	const { id, pendingStatus } = useSelector((state: RootState) => state.customerEnergyProfile);
	const hasReduxProfile = !!id;

	const { data: fetched, isLoading } = useGetEnergyAccountQuery(undefined, { skip: hasReduxProfile });

	useEffect(() => {
		if (!hasReduxProfile && fetched && id === "") {
			dispatch(
				setEnergyProfileInfo({
					id: fetched.id,
					isDeleted: fetched.isDeleted,
					userId: fetched.userId,
					meterNumber: fetched.meterNumber ?? fetched.accountNumber,
					accountNumber: fetched.accountNumber,
					meterAddress: fetched.meterAddress,
					utilityId: fetched.utilityId,
					utility: fetched.utility ?? null,
					consumerType: fetched.consumerType,
					pendingStatus: fetched.pendingStatus ?? null,
					preferRemoteTokenLoad: fetched.preferRemoteTokenLoad ?? null,
					isNinVerified: fetched.isNinVerified ?? false,
					isNinPhoneNumberVerified: fetched.isNinPhoneNumberVerified ?? false,
					residencyStatus: fetched.residencyStatus ?? null,
					nin: fetched.nin ?? null,
					createdAt: fetched.createdAt,
					updatedAt: fetched.updatedAt,
				})
			);
		}
	}, [hasReduxProfile, fetched, id, dispatch]);

	const effectiveProfile = hasReduxProfile ? { id, pendingStatus } : fetched;
	const hasValidEnergyProfile = !!effectiveProfile?.id && effectiveProfile.pendingStatus !== PendingStatus.Pending;

	const isSetupRoute =
		location.pathname.endsWith(`/${ApplicationRoutes.ENERGY_PROFILE_WELCOME}`) ||
		location.pathname.endsWith(`/${ApplicationRoutes.ENERGY_PROFILE_CREATE}`);

	// Wait for the fallback fetch to settle before deciding, so we don't redirect on a momentarily-empty Redux.
	if (!hasReduxProfile && isLoading) {
		return <CustomerLoader title="Checking energy profile" subtitle="" />;
	}

	// Setup routes (welcome / create): a customer who already has a profile can't set up another one.
	if (isSetupRoute) {
		return hasValidEnergyProfile ? (
			<Navigate to={`/${ApplicationRoutes.SETTINGS}/${ApplicationRoutes.CUSTOMER_ENERGY_PROFILE}`} replace />
		) : (
			<Outlet />
		);
	}

	// Protected routes require a valid profile.
	return hasValidEnergyProfile ? (
		<Outlet />
	) : (
		<Navigate to={`/${ApplicationRoutes.SETTINGS}/${ApplicationRoutes.ENERGY_PROFILE_WELCOME}`} replace />
	);
});

export default EnergyProfileGuard;
